import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('wasteProvider');
export interface WasteProviderInfoRec {
  id: string;
  userId: string;
  companyName?: string;
  serviceType?: string;
  accountNumber?: string;
  serviceAddress?: string;
  billingAddress?: string;
  customerServicePhone?: string;
  emergencyPhone?: string;
  website?: string;
  username?: string;
  password?: string;
  pickupSchedule?: string;
  monthlyCost?: number;
  autopayEnabled?: boolean;
  autopayMethod?: string;
  contractEndDate?: string;
  notes?: string;
  isLocked?: boolean;
}

class WasteProviderInfoService {
  async getAllWasteProviderInfo(targetUserId?: string): Promise<WasteProviderInfoRec[]> {
    const url = targetUserId
      ? `/api/waste-provider-info?targetUserId=${targetUserId}`
      : '/api/waste-provider-info';
    const response = await http.get(url);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch waste provider information');
    }

    return response.data.data || [];
  }

  async getWasteProviderInfoById(id: string): Promise<WasteProviderInfoRec | null> {
    try {
      const response = await http.get(`/api/waste-provider-info/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch waste provider information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching waste provider information by ID:', error);
      return null;
    }
  }

  async createWasteProviderInfo(wasteProviderData: Partial<WasteProviderInfoRec>): Promise<WasteProviderInfoRec> {
    const response = await http.post('/api/waste-provider-info', wasteProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save waste provider information');
    }

    return response.data.data;
  }

  async updateWasteProviderInfo(id: string, wasteProviderData: Partial<WasteProviderInfoRec>): Promise<WasteProviderInfoRec> {
    const response = await http.patch(`/api/waste-provider-info/${id}`, wasteProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update waste provider information');
    }

    return response.data.data;
  }

  async deleteWasteProviderInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/waste-provider-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete waste provider information');
    }
  }

  async lockWasteProviderInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for waste provider ID:', id);
    const response = await http.post(`/api/waste-provider-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock waste provider information');
    }
  }

  async unlockWasteProviderInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/waste-provider-info/${id}/unlock`, { granteeEmails });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock waste provider information');
    }
  }

  async getWasteProviderAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/waste-provider-info/${id}/access-grants`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }
}

export const wasteProviderInfoService = new WasteProviderInfoService();
export default wasteProviderInfoService;